import { Injectable } from '@angular/core';
import { DetachedRouteHandle } from '@angular/router';

/**
 * RouteCacheManagerService
 *
 * Manages bounded in-memory storage of Angular DetachedRouteHandles (FR-3).
 * Keyed by route URL path string.
 *
 * A configurable maximum size (MAX_CACHE_SIZE = 10) ensures memory safety
 * (NFR: memory-safe, bounded cache). When the limit is reached, the oldest
 * entry is evicted before storing the new handle.
 *
 * Engineers can call clearAll() at any time to programmatically clear the
 * cache (FR-6).
 */
@Injectable({ providedIn: 'root' })
export class RouteCacheManagerService {
    /** Maximum number of route handles kept alive in memory simultaneously. */
    private readonly MAX_CACHE_SIZE = 10;

    private readonly cache = new Map<string, DetachedRouteHandle>();

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Stores a detached route handle under `path`.
     * Evicts the oldest (first inserted) entry if the cache is full.
     */
    store(path: string, handle: DetachedRouteHandle): void {
        if (this.cache.size >= this.MAX_CACHE_SIZE) {
            const oldestKey = this.cache.keys().next().value;
            if (oldestKey !== undefined) {
                this.cache.delete(oldestKey);
            }
        }
        this.cache.set(path, handle);
    }

    /**
     * Returns the cached handle for `path`, or null if not found.
     */
    retrieve(path: string): DetachedRouteHandle | null {
        return this.cache.get(path) ?? null;
    }

    /**
     * Returns true if a handle is currently cached for `path`.
     */
    has(path: string): boolean {
        return this.cache.has(path);
    }

    /**
     * Clears the cached handle for a specific `path`, or clears the entire
     * cache when called without arguments.
     */
    clear(path?: string): void {
        if (path !== undefined) {
            this.cache.delete(path);
        } else {
            this.cache.clear();
        }
    }

    /**
     * Convenience alias for clearing all cached handles.
     * Exposed for engineer use via DI (FR-6).
     */
    clearAll(): void {
        this.cache.clear();
    }

    /** Returns the number of currently cached handles. */
    get size(): number {
        return this.cache.size;
    }
}
